import * as THREE from "three";
import ClipperLib from "clipper-lib";
import SceneGeometry from "../sceneObject/SceneGeometry";
import ChecklistItemData from "../sceneObjectData/ChecklistItemData";
import ChecklistItemTypeData from "../sceneObjectData/ChecklistItemTypeData";
import SceneObjectData from "../sceneObjectData/SceneObjectData";
import { RenderOrderEnum } from "../../enums/RenderOrderEnum";
import ShapeGeometry2d from "../geometry/ShapeGeometry2d";
import ShapeRepresentation2d from "../representation2d/ShapeRepresentation2d";
import LayerMappingsEnum from "../../enums/LayerMappingsEnum";
import { SceneObjectStyle } from "../sceneObject/SceneObjectStyle";
import { useModelPropertiesStore } from "../../store/modelPropertiesStore/modelPropertiesStore";

const ICON_SIZE = 0.3;

const DEFAULT_FILL_COLOR = 0x00ff00; // Bright green
const EDGE_COLOR = 0x696969; // Dim gray
const edgeThickness = 0.07; // Thickness of the checkmark edge

// Checkmark ✓ as a filled polygon, defined in normalized [-0.5, 0.5] space
const CHECKMARK_EDGE_POINTS: THREE.Vector2[] = [
    new THREE.Vector2(-0.1,  0.0),  // valley
    new THREE.Vector2(-0.3,  0.2),  
    new THREE.Vector2(-0.5, -0.0),  // left most point
    new THREE.Vector2(-0.1, -0.4),  // lowest point
    new THREE.Vector2( 0.5, 0.2),   // right most point
    new THREE.Vector2( 0.3, 0.4),  // valley outer corner
];

CHECKMARK_EDGE_POINTS[CHECKMARK_EDGE_POINTS.length] = CHECKMARK_EDGE_POINTS[0].clone(); // Close the loop

//const centroid = new THREE.Vector2(-0.1, -0.2); // Approximate centroid of the checkmark shape, used for offsetting edge points outward

/* const CHECKMARK_FILL_POINTS = CHECKMARK_EDGE_POINTS.map(p => {
    const dir = centroid.clone().sub(p).normalize(); // Direction from centroid to point
    return p.clone().add(dir.multiplyScalar(edgeThickness)); // Move point outward by edgeThickness 
}); */

/* const DEBUG_CHECKMARK_POINTS: THREE.Vector2[] = [
    new THREE.Vector2(-0.1,  0.4),  // outer tip of short arm (upper-left)
    new THREE.Vector2(0.0,  0.5),  // inner tip of short arm (upper-left)    new THREE.Vector2( 0.0, -0.15),  // valley inner corner
    new THREE.Vector2( 0.1,  0.4),   // inner tip of long arm (upper-right)
    new THREE.Vector2( 0.1,  0.1),   // outer tip of long arm (upper-right)
    
    new THREE.Vector2( 0.2, 0.1),  // valley outer corner
    new THREE.Vector2( 0.2, 0.0),  // valley outer corner
    
    new THREE.Vector2( -0.1, 0.0),  // valley outer corner
    new THREE.Vector2(-0.1, 0.4),  // outer base of short arm
]; */



export default class ChecklistItemSubGeometry2d extends SceneGeometry<ChecklistItemData> {

    private readonly edgeMaterial = new THREE.MeshBasicMaterial({
        color: EDGE_COLOR,
        side: THREE.FrontSide,
        depthTest: false,
    });

    private readonly defaultFillMaterial = new THREE.MeshBasicMaterial({
        color: DEFAULT_FILL_COLOR,
        side: THREE.FrontSide,
        depthTest: false,
    });

    constructor() {
        super();

        const edge = ShapeGeometry2d.create(
            new ShapeRepresentation2d(CHECKMARK_EDGE_POINTS, [], null),
            this.edgeMaterial,
            LayerMappingsEnum.Annotations2d,
            new THREE.Vector3(),
            true,
        );

        if (edge) {
            edge.scale.set(ICON_SIZE, ICON_SIZE, ICON_SIZE);
            edge.traverse((obj) => { obj.renderOrder = RenderOrderEnum.Annotation2d; });
            edge.rotateZ(-Math.PI / 2); 
            this.group.add(edge);
        }

        const fillPoints = this.offsetPolygon(  CHECKMARK_EDGE_POINTS, 
                                                -edgeThickness);
        
        const fill = ShapeGeometry2d.create(
            new ShapeRepresentation2d(fillPoints, [], null),
            this.defaultFillMaterial,
            LayerMappingsEnum.Annotations2d,
            new THREE.Vector3(),
            true,
        );

        if (fill) {
            fill.scale.set(ICON_SIZE, ICON_SIZE, ICON_SIZE);
            fill.traverse((obj) => { obj.renderOrder = RenderOrderEnum.Annotation2d; });
            fill.rotateZ(-Math.PI / 2); 
            this.group.add(fill);
        }
    }

    setStyle(style: SceneObjectStyle): void {
        if (this.style === style) return;
        this.edgeMaterial.color.setHex(style.color);
        this.style = style;
    }

    onAnnotationDirectionChange(rotationOffset: number): void {

        // Commented for now. Lets see if this is needed at all
        /* this.rotationOffset = rotationOffset;
        if (this.data)
            this.updateGeometry(this.data, true); */
    }

    protected updateGeometry(data: SceneObjectData, _forceUpdate: boolean): boolean {
        const checklistData = data as ChecklistItemData;
        this.group.position.copy(checklistData.position);

        // Cancel the parent storey's world rotation, then re-apply the model's
        // display rotation offset so the symbol aligns with the model orientation.
        const parent = this.group.parent;
        if (parent) {
            const parentWorldQuat = new THREE.Quaternion();
            parent.getWorldQuaternion(parentWorldQuat);
            this.group.quaternion.copy(parentWorldQuat.invert());
        }
        const rotationOffset = useModelPropertiesStore.getState().rotationOffset;
        const zAxis = new THREE.Vector3(0, 0, 1);
        // +π/2 corrects for the -π/2 baked into the child meshes interacting with the group quaternion
        const correctionQuat = new THREE.Quaternion().setFromAxisAngle(zAxis, Math.PI / 2 + rotationOffset);
        this.group.quaternion.multiply(correctionQuat);

        const typeData = checklistData.sceneObjectTypeData as ChecklistItemTypeData | null;
        this.defaultFillMaterial.color.set(typeData?.color ?? DEFAULT_FILL_COLOR);

        this.data = checklistData.clone();
        return true;
    }

    private offsetPolygon(points: THREE.Vector2[], offset: number): THREE.Vector2[] {
        const SCALE = 1000;

        const path = points.map(p => ({ X: Math.round(p.x * SCALE), Y: Math.round(p.y * SCALE) }));

        const co = new ClipperLib.ClipperOffset();
        co.AddPath(path, ClipperLib.JoinType.jtMiter, ClipperLib.EndType.etClosedPolygon);

        const solution: { X: number; Y: number }[][] = [];
        co.Execute(solution, offset * SCALE);

        if (solution.length === 0) return [];

        return solution[0].map(pt => new THREE.Vector2(pt.X / SCALE, pt.Y / SCALE));
    }


}
